<script setup lang="ts">
import type {
  ApprovableOptions,
  ButtonEmit,
  ButtonIconOptions,
  ButtonProps,
} from '/#/components/base/button/button'
import type { IconName, IconSizes } from '/#/components/base/icon/icon'
import type { ComputedRef, Ref, VNode } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import isBoolean from 'lodash-es/isBoolean'
import isString from 'lodash-es/isString'
import isUndefined from 'lodash-es/isUndefined'
import merge from 'lodash-es/merge'

import Icon from '/@/components/base/icon/Icon.vue'
import Link from '/@/components/base/link/Link.vue'
import DotLoader from '/@/components/base/loader/DotLoader.vue'
import Spinner from '/@/components/base/loader/Spinner.vue'

const props = withDefaults(defineProps<ButtonProps>(), {
  color: 'primary',
  variant: 'solid',
  size: 'md',
  type: 'button',
  to: undefined,
  text: undefined,
  approvable: undefined,
  tooltip: undefined,
  circle: false,
})

const emit = defineEmits<ButtonEmit>()

const slots = useSlots()
const attrs = useAttrs()
const { t } = useI18n()

/**
 * @name loader
 * @description Holds loader component.
 */
const loader = {
  spinner: Spinner,
  'dot-loader': DotLoader,
}

const appearance: ComputedRef<Array<string | boolean>> = computed(() => [
  props.color,
  props.variant,
  props.size,
  props.loading && 'loading',
  props.disabled && 'disabled',
  props.fullWidth && 'w-full',
  props.noPadding && '!p-0',
  props.circle && '!rounded-full circle',
  props.icon && !props.text ? 'just-icon-button' : '',
])

const defaultApprovableOptions: ComputedRef<ApprovableOptions> = computed(() => ({
  status: false,
  title: t('line-approve.default-title'),
  description: undefined,
  type: 'modal',
  confirmText: t('action.yes'),
  rejectText: t('action.no'),
  loading: false,
}))

const defaultIconOptions: ComputedRef<Omit<ButtonIconOptions, 'name'>> = computed(() => ({
  color: 'inherit',
  size: 'sm',
  position: 'before',
  class: ['button-icon'],
}))

const approveOptions: ComputedRef<ApprovableOptions> = computed(() => {
  return merge(defaultApprovableOptions.value, props.approvable)
})

const iconOptions: ComputedRef<ButtonIconOptions> = computed(() => {
  if (isString(props.icon)) {
    return merge({ name: props.icon }, defaultIconOptions.value) as ButtonIconOptions
  }

  return merge(defaultIconOptions.value, props.icon) as ButtonIconOptions
})

/**
 * @name iconComponent
 * @description: The icon component for button
 * @returns {VNode} or Button icon
 */
const iconComponent = (): VNode =>
  h(Icon, {
    name: iconOptions.value.name as IconName,
    size: iconOptions.value.size as IconSizes,
    class: iconOptions.value.class,
    color: iconOptions.value.color,
  })

/**
 * @name spinner
 * @description Spinner component for button.
 * @param {Spinner|DotLoader} component Holds component.
 * @returns {VNode}
 */
const spinner = (component: Component): VNode => h(component)

/**
 * Button content
 * @returns {VNode} or Button content
 */
const buttonContent = computed(() => {
  const textContent = slots.default?.() || props.text
  const content = [textContent]

  if (!isUndefined(props.icon)) {
    // Add icon before text content
    if (iconOptions.value.position === 'before') content.unshift([iconComponent()])
    // Add icon after text content
    if (iconOptions.value.position === 'after') content.push([iconComponent()])
  }

  if (props.loading) {
    const spinnerType = props.spinnerType || (props.circle ? 'spinner' : 'dot-loader')
    content.push([spinner(loader[spinnerType])])
  }

  return content
})

const approveCondition: ComputedRef<boolean | undefined> = computed(() => {
  if (isUndefined(props.approvable)) return false
  return isBoolean(props.approvable) ? props.approvable : approveOptions.value.status
})

/**
 * Button with link
 * @returns {VNode}
 */
const linkButton = (): VNode =>
  h(Link, { ...attrs, to: props.to as RouteLocationRaw, hideIcon: true }, () => [
    buttonContent.value,
  ])

/**
 * Button with static type
 * @returns {VNode}
 */
const staticButton = (): VNode =>
  h(
    'button',
    {
      ...attrs,
      type: props.type,
      disabled: props.disabled || props.loading,
    },
    [buttonContent.value],
  )

/**
 * Button component
 * @description: Button component with link or static type to props
 * @returns {VNode}
 */
const ButtonComponent = (): VNode => {
  if (props.to) {
    return linkButton()
  }

  return staticButton()
}

/**
 * @name lineApproveShow
 * @description: Line approve show state.
 */
const lineApproveShow: Ref<boolean> = ref(false)

/**
 * @method showLineApprove
 * @description: Set line approve show state to true.
 */
function showLineApprove() {
  lineApproveShow.value = true
}

/**
 * @method emitLineApproveSelection
 * @description: Emit line approve selection and hide line approve.
 * @param selection
 */
function emitLineApproveSelection(selection: 'approve' | 'reject') {
  if (selection === 'approve') emit('approve')
  else emit('reject')

  lineApproveShow.value = false
}

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <template v-if="approveCondition">
    <template v-if="approveOptions.type === 'modal'">
      <PopApprove
        :title="approveOptions.title"
        :description="approveOptions.description"
        :approve-button-text="approveOptions.confirmText"
        :reject-button-text="approveOptions.rejectText"
        :loading="approveOptions.loading"
        @approve="emit('approve')"
        @reject="emit('reject')"
      >
        <template #default="{ show }">
          <ButtonComponent
            v-tippy="tooltip"
            class="button"
            :class="[appearance]"
            :disabled="props.loading"
           
            @click.prevent.stop="show"
          />
        </template>
      </PopApprove>
    </template>

    <template v-else>
      <LineApprove
        v-if="lineApproveShow"
        :title="approveOptions.title"
        :loading="approveOptions.loading"
        remove-item-id="1"
        item-id="1"
        inline-mode
        align="space-between"
        @approve="emitLineApproveSelection('approve')"
        @reject="emitLineApproveSelection('reject')"
      />
      <template v-else>
        <ButtonComponent
          v-tippy="tooltip"
          class="button"
          :class="[appearance]"
          :disabled="props.loading"
         
          @click="showLineApprove"
        />
      </template>
    </template>
  </template>

  <ButtonComponent
    v-else
    v-tippy="tooltip"
   
    class="button"
    :class="[appearance]"
    :disabled="props.loading"
  />
</template>
